import {
  adminPostSchema,
  adminPostSummarySchema,
  EMPTY_DOC,
  postCategorySchema,
  postState,
  type AdminPost,
  type AdminPostSummary,
  type PostCategory,
  type PostState,
  type RichDoc,
} from '@growup/site-shared';
import {
  Button,
  Card,
  type Column,
  DateTimeInput,
  Field,
  Input,
  Notice,
  Select,
  StatusBadge,
  type StatusTone,
  Table,
  Textarea,
} from '@growup/ui';
import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { z } from 'zod';

import { ApiError, getJson, messageOf, send } from '../api';
import { isoToLisbonInput, lisbonInputToIso } from '../events/labels';
import { formatDateTime } from '../format';
import { IMAGE_ASPECTS, ImageField, useImagesBusy } from '../media/ImageUpload';
import { RichEditor } from './RichEditor';

export const POST_STATE_LOOK: Record<PostState, { label: string; tone: StatusTone }> = {
  draft: { label: 'Rascunho', tone: 'neutral' },
  scheduled: { label: 'Agendada', tone: 'info' },
  published: { label: 'Publicada', tone: 'success' },
  archived: { label: 'Arquivada', tone: 'neutral' },
};

export const autoSlug = (value: string) =>
  value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 80)
    .replace(/-$/, '');

function columns(categories: PostCategory[]): Column<AdminPostSummary>[] {
  return [
    {
      key: 'title',
      header: 'Notícia',
      cell: (p) => (
        <span className="flex flex-col">
          <span className="font-semibold">{p.title}</span>
          <span className="text-caption text-gu-ink-muted">
            {categories.find((c) => c.id === p.categoryId)?.name ?? 'Sem categoria'} · por{' '}
            {p.createdBy}
          </span>
        </span>
      ),
    },
    {
      key: 'date',
      header: 'Data',
      align: 'right',
      cell: (p) => (p.publishAt ? formatDateTime(p.publishAt) : '—'),
    },
    {
      key: 'status',
      header: 'Estado',
      align: 'right',
      cell: (p) => {
        const look = POST_STATE_LOOK[postState(p)];
        return <StatusBadge tone={look.tone}>{look.label}</StatusBadge>;
      },
    },
  ];
}

/** Noticias: lista, escrever, publicar ou agendar. */
export function PostsView() {
  const [list, setList] = useState<AdminPostSummary[] | null>(null);
  const [categories, setCategories] = useState<PostCategory[]>([]);
  const [selected, setSelected] = useState<string | 'new' | null>(null);
  const [post, setPost] = useState<AdminPost | null>(null);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    getJson('/api/site/content/posts', z.array(adminPostSummarySchema))
      .then(setList)
      .catch((e: unknown) => setError(messageOf(e)));
    getJson('/api/site/content/categories', z.array(postCategorySchema))
      .then(setCategories)
      .catch((e: unknown) => setError(messageOf(e)));
  }, []);
  useEffect(load, [load]);

  useEffect(() => {
    setPost(null);
    if (selected === null || selected === 'new') return;
    getJson(`/api/site/content/posts/${selected}`, adminPostSchema)
      .then(setPost)
      .catch((e: unknown) => setError(messageOf(e)));
  }, [selected]);

  const close = () => {
    setSelected(null);
    load();
  };

  if (selected !== null) {
    if (selected !== 'new' && post === null) {
      return error ? <Notice tone="danger">{error}</Notice> : <p>A carregar…</p>;
    }
    const look = post ? POST_STATE_LOOK[postState(post)] : null;
    return (
      <div className="flex flex-col gap-4">
        <div>
          <Button variant="ghost" size="sm" onClick={close}>
            ← Notícias
          </Button>
        </div>
        {post && look ? (
          <div className="flex flex-wrap items-center gap-3">
            <h2 className="font-display text-title uppercase">{post.title}</h2>
            <StatusBadge tone={look.tone}>{look.label}</StatusBadge>
          </div>
        ) : null}
        <div className="grid items-start gap-4 xl:grid-cols-[1fr_22rem]">
          <PostForm
            key={post?.id ?? 'new'}
            post={post}
            categories={categories}
            onSaved={(saved) => {
              setPost(saved);
              setSelected(saved.id);
            }}
          />
          {post ? <PublishPanel post={post} onSaved={setPost} onDeleted={close} /> : null}
        </div>
      </div>
    );
  }

  return (
    <Card
      headingLevel={2}
      title="Notícias"
      actions={
        <Button size="sm" onClick={() => setSelected('new')}>
          Nova notícia
        </Button>
      }
    >
      {error ? <Notice tone="danger">{error}</Notice> : null}
      <Table
        caption="Notícias"
        columns={columns(categories)}
        rows={list ?? []}
        rowKey={(p) => p.id}
        empty={list ? 'Ainda não há notícias.' : 'A carregar…'}
        onRowClick={(p) => setSelected(p.id)}
      />
    </Card>
  );
}

function PostForm({
  post,
  categories,
  onSaved,
}: {
  post: AdminPost | null;
  categories: PostCategory[];
  onSaved: (p: AdminPost) => void;
}) {
  const [title, setTitle] = useState(post?.title ?? '');
  const [slug, setSlug] = useState(post?.slug ?? '');
  const [summary, setSummary] = useState(post?.summary ?? '');
  const [categoryId, setCategoryId] = useState(post?.categoryId ?? categories[0]?.id ?? '');
  const [coverUrl, setCoverUrl] = useState<string | null>(post?.coverUrl ?? null);
  const [coverAlt, setCoverAlt] = useState(post?.coverAlt ?? '');
  const [body, setBody] = useState<RichDoc>(post?.body ?? EMPTY_DOC);
  const [fields, setFields] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const photos = useImagesBusy();

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    const payload = {
      slug,
      title,
      summary,
      body,
      coverUrl,
      coverAlt: coverUrl ? coverAlt : '',
      categoryId: categoryId === '' ? null : categoryId,
    };
    setBusy(true);
    setError(null);
    setSaved(null);
    setFields({});
    try {
      const result = post
        ? await send('PUT', `/api/site/content/posts/${post.id}`, payload)
        : await send('POST', '/api/site/content/posts', payload);
      onSaved(adminPostSchema.parse(result));
      setSaved(post ? 'Guardado.' : 'Criada como rascunho. Publique quando estiver pronta.');
    } catch (err) {
      setError(messageOf(err));
      if (err instanceof ApiError) setFields(err.fields);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card headingLevel={2} title={post ? 'Texto da notícia' : 'Nova notícia'}>
      <form className="flex flex-col gap-4" onSubmit={(e) => void submit(e)} noValidate>
        <div className="grid gap-3 sm:grid-cols-2">
          <Field label="Título" error={fields.title}>
            <Input
              value={title}
              maxLength={140}
              onChange={(e) => {
                setTitle(e.target.value);
                if (!post) setSlug(autoSlug(e.target.value));
              }}
            />
          </Field>
          <Field label="Endereço" hint={`growup.gg/noticias/${slug || '…'}`} error={fields.slug}>
            <Input value={slug} onChange={(e) => setSlug(e.target.value)} />
          </Field>
          <Field label="Categoria" error={fields.categoryId}>
            <Select value={categoryId} onChange={(e) => setCategoryId(e.target.value)}>
              <option value="">Sem categoria</option>
              {categories.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </Select>
          </Field>
        </div>
        <Field
          label="Resumo"
          optional
          hint="Uma ou duas frases: aparece na lista, na página inicial e quando se partilha o link. Vazio = o início do texto."
          error={fields.summary}
        >
          <Textarea
            rows={2}
            maxLength={300}
            value={summary}
            onChange={(e) => setSummary(e.target.value)}
          />
        </Field>
        <div className="grid gap-3 sm:grid-cols-2">
          <ImageField
            label="Imagem de capa"
            optional
            value={coverUrl}
            onChange={setCoverUrl}
            aspect={IMAGE_ASPECTS.newsCover}
            onBusyChange={photos.track('capa')}
            error={fields.coverUrl}
            hint="Também aparece quando se partilha o link (WhatsApp, Discord, Facebook)."
          />
          {coverUrl ? (
            <Field
              label="Descrição da capa"
              hint="Para quem não vê a imagem. Ex.: «A equipa de Valorant a levantar a taça»."
              error={fields.coverAlt}
            >
              <Input
                value={coverAlt}
                maxLength={300}
                onChange={(e) => setCoverAlt(e.target.value)}
              />
            </Field>
          ) : null}
        </div>
        <RichEditor
          label="Texto"
          value={body}
          onChange={setBody}
          error={fields.body ?? fields['']}
          hint="Cole à vontade de outro sítio: fica só a formatação que o site mostra."
        />
        {error ? <Notice tone="danger">{error}</Notice> : null}
        {saved ? <Notice tone="success">{saved}</Notice> : null}
        <div>
          <Button type="submit" disabled={busy || photos.busy}>
            {post ? 'Guardar' : 'Criar rascunho'}
          </Button>
        </div>
      </form>
    </Card>
  );
}

function PublishPanel({
  post,
  onSaved,
  onDeleted,
}: {
  post: AdminPost;
  onSaved: (p: AdminPost) => void;
  onDeleted: () => void;
}) {
  const state = postState(post);
  const [when, setWhen] = useState(isoToLisbonInput(post.publishAt));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const base = `/api/site/content/posts/${post.id}`;

  const run = async (path: string, body?: unknown) => {
    setBusy(true);
    setError(null);
    try {
      onSaved(adminPostSchema.parse(await send('POST', `${base}${path}`, body)));
    } catch (e) {
      setError(messageOf(e));
    } finally {
      setBusy(false);
    }
  };

  const schedule = () => {
    const iso = lisbonInputToIso(when);
    if (iso === null) {
      setError('Indique a data e a hora.');
      return;
    }
    void run('/publish', { publishAt: iso });
  };

  const remove = async () => {
    if (!window.confirm(`Apagar «${post.title}»? Não se pode desfazer.`)) return;
    setBusy(true);
    try {
      await send('DELETE', base);
      onDeleted();
    } catch (e) {
      setError(messageOf(e));
      setBusy(false);
    }
  };

  return (
    <Card headingLevel={2} title="Publicação">
      <div className="flex flex-col gap-3">
        <p className="text-gu-ink-muted">
          {state === 'draft' ? 'Rascunho: ainda não aparece no site.' : null}
          {state === 'scheduled' && post.publishAt
            ? `Agendada: aparece no site a ${formatDateTime(post.publishAt)}.`
            : null}
          {state === 'published' && post.publishAt
            ? `No site desde ${formatDateTime(post.publishAt)}.`
            : null}
          {state === 'archived' ? 'Arquivada: saiu do site.' : null}
        </p>
        <p className="text-caption text-gu-ink-muted">
          Guarde o texto antes de publicar. Última alteração: {formatDateTime(post.updatedAt)} por{' '}
          {post.updatedBy}.
        </p>
        {state === 'published' ? (
          <a className="gu-link" href={`/noticias/${post.slug}`} target="_blank" rel="noreferrer">
            Ver no site
          </a>
        ) : null}
        {state !== 'published' ? (
          <Button disabled={busy} onClick={() => void run('/publish', { publishAt: null })}>
            Publicar agora
          </Button>
        ) : null}
        <div className="flex flex-col gap-2 border-t border-gu-line pt-3">
          <Field label={state === 'scheduled' ? 'Mudar a data' : 'Agendar'} hint="Hora de Lisboa.">
            <DateTimeInput value={when} onChange={setWhen} />
          </Field>
          <Button variant="secondary" disabled={busy || when === ''} onClick={schedule}>
            {state === 'published' ? 'Mudar a data de publicação' : 'Agendar'}
          </Button>
        </div>
        {state === 'published' || state === 'scheduled' ? (
          <div className="flex flex-col gap-2 border-t border-gu-line pt-3">
            <Button variant="secondary" disabled={busy} onClick={() => void run('/unpublish')}>
              Voltar a rascunho
            </Button>
            <Button variant="ghost" disabled={busy} onClick={() => void run('/archive')}>
              Arquivar (tirar do site)
            </Button>
          </div>
        ) : (
          <div className="border-t border-gu-line pt-3">
            <Button variant="ghost" disabled={busy} onClick={() => void remove()}>
              Apagar
            </Button>
          </div>
        )}
        {error ? <Notice tone="danger">{error}</Notice> : null}
      </div>
    </Card>
  );
}
